import { Delivery } from '~api/som';
import { GetTrackingEventsResponse } from '~api/wimo';

import { Card, ListLayout, Progress } from '@ingrid/luterkort-react';

import { LocalizedDate } from '~src/i18n/localized-date';
import { useTranslation } from '~src/i18n/use-translation';
import { getTrackingEventStatusLabel } from '~src/utils/tracking-event-status-helpers';

import { useGetTrackingEventsQuery } from '../queries';
import { ShipmentDetails } from '../types';
import { DownloadConsolidatedLabelButton } from './download-consolidated-label-button';
import { DownloadLabelButton } from './download-label-button';
import { DownloadQrCodeButton } from './download-qr-code-button';

interface Props {
  shipment: Pick<ShipmentDetails, 'deliveries' | 'status' | 'direction_type'>;
}

export function Deliveries({ shipment }: Props) {
  const { data } = useGetTrackingEventsQuery({
    delivery_ids: shipment.deliveries.map(d => d.id!),
  });

  const { t } = useTranslation();

  if (!data.length) {
    return null;
  }

  const deliveriesWithTrackingEvents = shipment.deliveries
    .map(d => ({
      ...d,
      trackingEvent: data.find(t => t.logistic_units?.[0].delivery_id === d.id),
    }))
    .filter((d): d is Delivery & { trackingEvent: GetTrackingEventsResponse } => !!d.trackingEvent);

  return (
    <Card.Wrapper>
      <Card.Header>{t('common.deliveries')}</Card.Header>
      {deliveriesWithTrackingEvents.map(({ trackingEvent: { logistic_units }, label_url, documents }) => {
        const events = logistic_units?.[0].tracking_events ?? [];
        console.log('events', events.length - 1);

        return (
          <Card.Content key={logistic_units?.[0].delivery_id}>
            <ListLayout.Column>
              <ListLayout.Row>
                <ListLayout.Stack style={{ flex: 1 }}>
                  <ListLayout.Text variant="secondary">{t('common.sscc')}</ListLayout.Text>
                  <ListLayout.Text variant="primary">{logistic_units?.[0].tracking_number}</ListLayout.Text>
                </ListLayout.Stack>

                <DownloadQrCodeButton shipment={shipment} labelUrl={documents?.qr_code_url} size="sm" />
                <DownloadLabelButton shipment={shipment} labelUrl={label_url} size="sm" />
              </ListLayout.Row>

              {events.length && (
                <Progress.Root value={String(events.length - 1)}>
                  {events
                    .map((event, i) => (
                      <Progress.Item
                        // Since this is relying on flex-flow: column-reverse styling the first element would actually be the last
                        style={{ paddingBottom: i === 0 ? '0px' : '' }}
                        value={i.toString()}
                        key={event.event_id}
                      >
                        <Progress.ItemIndicator />
                        <Progress.Stack>
                          <Progress.PrimaryText>
                            {t(getTrackingEventStatusLabel(event.tracking_status))}
                          </Progress.PrimaryText>
                          <Progress.SecondaryText>
                            <LocalizedDate dateTime={event.created_at!} time />
                          </Progress.SecondaryText>
                        </Progress.Stack>
                      </Progress.Item>
                    ))
                    .reverse()}
                </Progress.Root>
              )}
            </ListLayout.Column>
          </Card.Content>
        );
      })}

      {shipment.deliveries.map(d => d.label_url).length >= 2 && (
        <Card.Content>
          <DownloadConsolidatedLabelButton shipment={shipment} />
        </Card.Content>
      )}
    </Card.Wrapper>
  );
}
